import { Box, Flex, Text, useBoolean } from '@chakra-ui/react';
import { Code } from 'constants/keyboard';
import {
  pageRangeToString,
  parseCustomPageRangeInput,
} from 'modules/Editor/components/CustomPageRange/CustomPageRange.helpers';
import { useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { selectPageCount, selectPageNumberCustomRange } from 'modules/Editor/store/infographSelector';
import { setPageNumberCustomPageRange } from 'modules/Editor/store/infographSlice';
import { Input, InputError } from 'modules/common/components/Input';
import { ChangeEvent, KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { inputWidth, labelWidth } from '../PageNumberMenu.config';

const customPageRangeTestId = 'custom-page-range';
const customPageRangeInputTestId = 'custom-page-range-input';

export const CustomPageRange = () => {
  const customPageRange = useAppSelector(selectPageNumberCustomRange);
  const pageCount = useAppSelector(selectPageCount);
  const dispatch = useAppDispatch();

  const [isValidRange, setIsValidRange] = useBoolean(true);
  const [customPageRangeInput, setCustomPageRangeInput] = useState(pageRangeToString(customPageRange));

  const inputId = 'custom-page-range';
  const inputErrorId = 'custom-page-range-error';

  const { t } = useTranslation('common_navbar');
  const inputError = t('pageRange.invalid-input-error');
  const customPageRangeText = t('pageNumber.custom-page-range');

  const onChangeCustomPageRange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value || '';
    setCustomPageRangeInput(inputValue);

    try {
      parseCustomPageRangeInput(inputValue, pageCount);
      setIsValidRange.on();
    } catch (err) {
      setIsValidRange.off();
    }
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === Code.Enter) setCustomPageRange();
  };

  const onBlurInput = () => setCustomPageRange();

  const setCustomPageRange = () => {
    if (isValidRange) {
      const customPageRange = parseCustomPageRangeInput(customPageRangeInput, pageCount);
      dispatch(setPageNumberCustomPageRange(customPageRange));
    }
  };

  return (
    <Flex direction='column'>
      <Flex gap={2} data-testid={customPageRangeTestId}>
        <Text fontSize='sm' w={labelWidth} as='label' htmlFor={inputId} fontWeight='medium'>
          {customPageRangeText}
        </Text>
        <Flex direction='column' gap={2}>
          <Input
            w={inputWidth}
            size={'xs'}
            errorBorderColor='red.600'
            value={customPageRangeInput}
            onChange={onChangeCustomPageRange}
            onKeyDown={onInputKeyDown}
            onBlur={onBlurInput}
            isInvalid={!isValidRange}
            cursor='text'
            id={inputId}
            data-testid={customPageRangeInputTestId}
            textAlign='right'
            aria-describedby={inputErrorId}
          />
        </Flex>
      </Flex>
      {!isValidRange && (
        <Box mt={1}>
          <InputError id={inputErrorId}>{inputError}</InputError>
        </Box>
      )}
    </Flex>
  );
};
